import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import NumberInput from './NumberInput';
import { Select } from './Select';

export type DmsCoordinateKind = 'latitude' | 'longitude';

const LAT_DIRECTIONS = [
  { value: 'N', label: 'North' },
  { value: 'S', label: 'South' },
];

const LON_DIRECTIONS = [
  { value: 'E', label: 'East' },
  { value: 'W', label: 'West' },
];

function normalizeDirection(raw: string, kind: DmsCoordinateKind): string {
  const s = raw.trim().toUpperCase();
  if (kind === 'latitude') {
    if (s === 'N' || s.startsWith('NORTH')) return 'N';
    if (s === 'S' || s.startsWith('SOUTH')) return 'S';
    return '';
  }
  if (s === 'E' || s.startsWith('EAST')) return 'E';
  if (s === 'W' || s.startsWith('WEST')) return 'W';
  return '';
}

export function parseDmsCoordinate(
  value: string,
  kind: DmsCoordinateKind,
): { degrees: string; minutes: string; direction: string } {
  const s = value.trim();
  if (!s) return { degrees: '', minutes: '', direction: '' };

  const dirPattern = kind === 'latitude' ? '(N|S|North|South)' : '(E|W|East|West)';
  const withDir = s.match(
    new RegExp(`^(\\d{1,3})\\s*[°]?\\s*(\\d+(?:\\.\\d+)?)\\s*['′]?\\s*${dirPattern}\\s*$`, 'i'),
  );
  if (withDir) {
    return {
      degrees: withDir[1],
      minutes: withDir[2],
      direction: normalizeDirection(withDir[3], kind),
    };
  }

  const withoutDir = s.match(/^(\d{1,3})\s*[°]?\s*(\d+(?:\.\d+)?)\s*['′]?\s*$/);
  if (withoutDir) {
    return { degrees: withoutDir[1], minutes: withoutDir[2], direction: '' };
  }

  return { degrees: '', minutes: '', direction: '' };
}

export function formatDmsCoordinate(degrees: string, minutes: string, direction: string): string {
  const d = degrees.trim();
  const m = minutes.trim();
  const dir = direction.trim();
  if (!d && !m) return '';
  const parts: string[] = [];
  if (d) parts.push(d);
  if (m) parts.push(m);
  if (dir) parts.push(dir);
  return parts.join(' ');
}

interface DmsCoordinateInputProps {
  kind: DmsCoordinateKind;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  className?: string;
}

const DmsCoordinateInput: React.FC<DmsCoordinateInputProps> = ({
  kind,
  value = '',
  onChange,
  disabled = false,
  readOnly = false,
  error,
  className,
}) => {
  const { colors } = useTheme();
  const parsed = parseDmsCoordinate(value, kind);
  const [degrees, setDegrees] = useState(parsed.degrees);
  const [minutes, setMinutes] = useState(parsed.minutes);
  const [direction, setDirection] = useState(parsed.direction);

  useEffect(() => {
    const next = parseDmsCoordinate(value, kind);
    setDegrees(next.degrees);
    setMinutes(next.minutes);
    setDirection(next.direction);
  }, [value, kind]);

  const emit = (d: string, m: string, dir: string) => {
    onChange?.(formatDmsCoordinate(d, m, dir));
  };

  const maxDegrees = kind === 'latitude' ? 90 : 180;
  const directionOptions = kind === 'latitude' ? LAT_DIRECTIONS : LON_DIRECTIONS;

  return (
    <div className={cn('w-full min-w-0', className)}>
      <div className="grid grid-cols-3 gap-2">
        <NumberInput
          value={degrees}
          onChange={(e) => {
            const next = e.target.value == null ? '' : String(e.target.value);
            setDegrees(next);
            emit(next, minutes, direction);
          }}
          placeholder="°"
          min={0}
          max={maxDegrees}
          step={1}
          disabled={disabled}
          readOnly={readOnly}
          aria-label={kind === 'latitude' ? 'Latitude degrees' : 'Longitude degrees'}
        />
        <NumberInput
          value={minutes}
          onChange={(e) => {
            const next = e.target.value == null ? '' : String(e.target.value);
            setMinutes(next);
            emit(degrees, next, direction);
          }}
          placeholder="'"
          min={0}
          max={59.999}
          step={0.1}
          disabled={disabled}
          readOnly={readOnly}
          aria-label={kind === 'latitude' ? 'Latitude minutes' : 'Longitude minutes'}
        />
        <Select
          value={direction}
          onChange={(e) => {
            const next = e.target.value;
            setDirection(next);
            emit(degrees, minutes, next);
          }}
          options={directionOptions}
          placeholder={kind === 'latitude' ? 'N/S' : 'E/W'}
          disabled={disabled}
          readOnly={readOnly}
          showClearButton={false}
          aria-label={kind === 'latitude' ? 'Latitude direction' : 'Longitude direction'}
        />
      </div>
      {error ? <div className={cn('mt-1 text-sm', colors.error)}>{error}</div> : null}
    </div>
  );
};

DmsCoordinateInput.displayName = 'DmsCoordinateInput';

export default DmsCoordinateInput;
